import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import logo from '../../assets/images/logo.png';
import authBg from '../../assets/images/auth-bg.jpg';
import { useAuth } from '../../hooks/useAuth';
import { authApi } from '../../api/auth';
import { GoogleLoginButton } from '../../components/GoogleLoginButton';

type Mode = 'login' | 'register' | 'forgot';

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const initialMode = (searchParams.get('mode') as Mode) || 'login';
  const [mode, setMode] = useState<Mode>(initialMode);
  const [showPass, setShowPass] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [success, setSuccess] = useState('');
  const [localError, setLocalError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const { login, register, isLoading, error, clearError, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) navigate('/library', { replace: true });
  }, [isAuthenticated, navigate]);

  const handleModeChange = (newMode: Mode) => {
    setMode(newMode);
    clearError();
    setLocalError('');
    setSuccess('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLocalError('');

    if (mode === 'login') {
      try {
        await login(form.email, form.password);
        navigate('/library', { replace: true });
      } catch {
        // error já está em auth.error
      }
    } else if (mode === 'register') {
      if (form.password !== form.confirm) {
        setLocalError('As senhas não coincidem.');
        return;
      }
      try {
        await register(form.name, form.email, form.password);
        setSuccess('Conta criada! Verifique seu email para ativar a conta antes de entrar.');
      } catch {
        // error já está em auth.error
      }
    } else {
      setForgotLoading(true);
      try {
        await authApi.resetPasswordRequest(form.email);
        setSuccess('Email de recuperação enviado! Verifique sua caixa de entrada.');
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { error?: { message?: string } } } })
            ?.response?.data?.error?.message ?? 'Erro ao enviar email. Tente novamente.';
        setLocalError(msg);
      } finally {
        setForgotLoading(false);
      }
    }
  };

  const displayError = localError || error;
  const submitting = isLoading || forgotLoading;

  return (
    <div className="min-h-screen bg-[#0A0A0F] flex">
      {/* Left Panel — Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col justify-between p-14">
        <img
          src={authBg}
          alt="background"
          className="absolute inset-0 w-full h-full object-cover object-top opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0F] via-[#0A0A0F]/70 to-transparent"></div>

        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img src={logo} alt="Escala Equipe" className="w-9 h-9 object-contain" />
            <span className="text-white font-bold text-xl" style={{ fontFamily: "'Playfair Display', serif" }}>Escala Equipe</span>
          </Link>
        </div>

        <div className="relative z-10">
          <blockquote className="text-white/70 text-xl font-light leading-relaxed mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            &ldquo;A música é a linguagem da alma. Escala Equipe é o instrumento que a conecta.&rdquo;
          </blockquote>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#C8A96E]/30 flex items-center justify-center">
              <i className="ri-music-2-line text-[#C8A96E] text-sm"></i>
            </div>
            <div>
              <p className="text-white text-sm font-medium">Ministério de Louvor</p>
              <p className="text-white/40 text-xs">10.000+ músicos ativos</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel — Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="flex lg:hidden items-center gap-3 mb-10">
            <img src={logo} alt="Escala Equipe" className="w-8 h-8 object-contain" />
            <span className="text-white font-bold text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>Escala Equipe</span>
          </div>

          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#C8A96E]/10 mx-auto mb-6">
                <i className="ri-check-line text-[#C8A96E] text-3xl"></i>
              </div>
              <h2 className="text-white text-2xl font-semibold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Pronto!</h2>
              <p className="text-white/50 text-sm mb-8">{success}</p>
              <Link to="/" className="text-[#C8A96E] text-sm hover:underline cursor-pointer">← Voltar para o início</Link>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h2 className="text-3xl font-light text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {mode === 'login' && 'Bem-vindo de volta'}
                  {mode === 'register' && 'Criar sua conta'}
                  {mode === 'forgot' && 'Recuperar senha'}
                </h2>
                <p className="text-white/40 text-sm">
                  {mode === 'login' && 'Entre na sua conta Escala Equipe'}
                  {mode === 'register' && 'Comece gratuitamente, sem cartão'}
                  {mode === 'forgot' && 'Enviaremos um link de recuperação'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                {mode === 'register' && (
                  <div>
                    <label className="text-white/50 text-xs uppercase tracking-widest mb-2 block">Nome completo</label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Seu nome"
                      required
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 outline-none focus:border-[#C8A96E]/50 transition-colors"
                    />
                  </div>
                )}

                <div>
                  <label className="text-white/50 text-xs uppercase tracking-widest mb-2 block">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="seu@email.com"
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 outline-none focus:border-[#C8A96E]/50 transition-colors"
                  />
                </div>

                {mode !== 'forgot' && (
                  <div>
                    <label className="text-white/50 text-xs uppercase tracking-widest mb-2 block">Senha</label>
                    <div className="relative">
                      <input
                        type={showPass ? 'text' : 'password'}
                        name="password"
                        value={form.password}
                        onChange={handleChange}
                        placeholder="••••••••"
                        required
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 pr-12 text-white text-sm placeholder-white/20 outline-none focus:border-[#C8A96E]/50 transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPass(!showPass)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 cursor-pointer w-5 h-5 flex items-center justify-center"
                      >
                        <i className={`ri-${showPass ? 'eye-off' : 'eye'}-line text-sm`}></i>
                      </button>
                    </div>
                  </div>
                )}

                {mode === 'register' && (
                  <div>
                    <label className="text-white/50 text-xs uppercase tracking-widest mb-2 block">Confirmar senha</label>
                    <input
                      type="password"
                      name="confirm"
                      value={form.confirm}
                      onChange={handleChange}
                      placeholder="••••••••"
                      required
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 outline-none focus:border-[#C8A96E]/50 transition-colors"
                    />
                  </div>
                )}

                {mode === 'login' && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => handleModeChange('forgot')}
                      className="text-white/40 hover:text-[#C8A96E] text-xs transition-colors cursor-pointer"
                    >
                      Esqueceu a senha?
                    </button>
                  </div>
                )}

                {displayError && (
                  <p className="text-red-400 text-xs text-center -mb-1">{displayError}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#C8A96E] text-[#0A0A0F] font-semibold py-3.5 rounded-xl hover:bg-[#D4B87A] transition-colors whitespace-nowrap cursor-pointer mt-2 disabled:opacity-60"
                >
                  {submitting ? 'Aguarde...' : (
                    mode === 'login' ? 'Entrar' :
                    mode === 'register' ? 'Criar conta' :
                    'Enviar link de recuperação'
                  )}
                </button>
              </form>

              {mode !== 'forgot' && (
                <>
                  <div className="flex items-center gap-3 my-6">
                    <div className="flex-1 h-px bg-white/10"></div>
                    <span className="text-white/30 text-xs uppercase tracking-wider">ou</span>
                    <div className="flex-1 h-px bg-white/10"></div>
                  </div>

                  <GoogleLoginButton />
                </>
              )}

              {/* Plan info for register */}
              {mode === 'register' && (
                <div className="mt-6 p-4 bg-white/3 border border-white/5 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-shield-check-line text-[#C8A96E] text-sm"></i>
                    <span className="text-white/60 text-xs font-medium">Plano Free incluído</span>
                  </div>
                  <p className="text-white/30 text-xs">1 usuário · 10 músicas · Sem cartão de crédito</p>
                </div>
              )}

              <div className="mt-8 text-center">
                {mode === 'login' && (
                  <p className="text-white/30 text-sm">
                    Não tem conta?{' '}
                    <button onClick={() => handleModeChange('register')} className="text-[#C8A96E] hover:underline cursor-pointer">Criar gratuitamente</button>
                  </p>
                )}
                {mode === 'register' && (
                  <p className="text-white/30 text-sm">
                    Já tem conta?{' '}
                    <button onClick={() => handleModeChange('login')} className="text-[#C8A96E] hover:underline cursor-pointer">Entrar</button>
                  </p>
                )}
                {mode === 'forgot' && (
                  <button onClick={() => handleModeChange('login')} className="text-white/30 hover:text-white text-sm cursor-pointer">
                    ← Voltar para o login
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
